import { hash } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { RadiobuttonItemInfo, Service, Slack, ValidationErrors } from 'webapp-types';
import { AlertEventCondition, DeliveryType, EventDeliveryTime } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonRecipientsSelectorComponent from 'webapp/components/common/recipients-selector';
import CommonUiIcon from 'webapp/components/common/ui-icon';
import FormInputDecimalComponent from 'webapp/components/form/input-decimal';
import FormInputPercentComponent from 'webapp/components/form/input-percent';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import FormSelectComponent from 'webapp/components/form/select';
import NotificationsAlertsCustomTemplateComponent from 'webapp/components/notifications/alerts/custom-template';
import NotificationsDeliveryTargetsComponent from 'webapp/components/notifications/delivery-targets';
import {
	type ChunkArgs as BaseChunkArgs,
	type ChunkInterface,
	type SaveFn,
	type ValidateFn,
} from 'webapp/components/notifications/edit/form/base';
import NotificationsEditFormMetricSetupRowComponent from 'webapp/components/notifications/edit/form/metric-setup-row';
import FormRowComponent from 'webapp/components/notifications/edit/form/row';
import RowErrorComponent from 'webapp/components/row-error';
import SectionWithTogglerComponent from 'webapp/components/section-with-toggler';

import type { Alert, MetricSetup } from 'webapp/models-custom';

type ValidationKeys = 'condition' | 'qualifier' | 'delivery' | 'metric' | 'unit' | 'attribute';
export type ValidationErrorsType = ValidationErrors<ValidationKeys>;

interface MainComponentArgs extends BaseChunkArgs {
	model: Alert;
	submitInProgress: boolean;
	errors: ValidationErrorsType;
	origin: string;
	setValidateFn?: (fn: ValidateFn) => void;
	setSaveFn?: (fn: SaveFn) => void;
	onContentChange?: () => void;
}

interface MainComponentInterface extends ChunkInterface {
	Args: MainComponentArgs;
}

export default class NotificationsEditFormAlertMainComponent extends Component<MainComponentInterface> {
	@service('constants') declare constantsService: Service.Constants;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	deliveryTimeOptions: RadiobuttonItemInfo[] = [
		{ value: EventDeliveryTime.ANYTIME, label: 'Any time' },
		{
			value: EventDeliveryTime.WEEKDAYS_WORKTIME,
			label: 'Weekdays only',
			tooltip: 'Between 8 AM and 5 PM',
			tooltipIcon: 'InfoFill',
		},
	];

	@tracked isAdvancedOpen = false;

	constructor(owner: Owner, args: MainComponentArgs) {
		super(owner, args);

		this.args.setValidateFn?.(this.validate);
		this.args.setSaveFn?.(this.save);
	}

	get metricSetup(): MetricSetup {
		return this.args.model.metricSetup;
	}

	get eventConditions() {
		return this.constantsService.eventConditions;
	}

	get qualifierString() {
		return typeof this.args.model.qualifier === 'number'
			? String(this.args.model.qualifier)
			: this.args.model.qualifier;
	}

	get isRecipientsShown() {
		return (
			this.args.model.deliveryType.includes(DeliveryType.EMAIL) ||
			this.args.model.deliveryType.includes(DeliveryType.PUSH)
		);
	}

	@action
	changeCondition(condition: AlertEventCondition) {
		if (condition) {
			this.args.model.condition = condition;
		}
	}

	@action
	changeQualifier(qualifier: number | null) {
		if (qualifier !== null && qualifier !== undefined) {
			this.args.model.qualifier = Number(qualifier);
		}
	}

	@action
	changeRange(rangeId: number) {
		this.args.model.selectedRangeId = rangeId;
	}

	@action
	changeDeliveryTimeType(type: AnySimpleValue) {
		this.args.model.deliveryTime = type as EventDeliveryTime;
	}

	@action
	changeDelivery(delivery: DeliveryType[], isFromSetDefaults = false) {
		this.args.model.deliveryType = delivery;
		if (isFromSetDefaults) {
			next(() => {
				this.args.model.applyBufferedChanges(['deliveryType']);
			});
		}

		this.args.resetErrorData?.('delivery');
	}

	@action
	changeSlackChannels(newChannels: Slack.Channels, isFromSetDefaults = false) {
		this.analyticsService.event(TrackingEvent.SLACK_CHANNELS_SELECTED, { origin: 'alert' });
		this.args.model.slackChannels = newChannels;
		if (isFromSetDefaults) {
			next(() => {
				this.args.model.applyBufferedChanges(['slackChannels']);
			});
		}
	}

	@action
	toggleAdvanced() {
		this.isAdvancedOpen = !this.isAdvancedOpen;
		this.args.onContentChange?.();
	}

	@action
	validate() {
		const errors: ValidationErrorsType = {};

		const model = this.args.model;

		model.validate(); // to cleanup general errors

		if (!model.condition) {
			errors.condition = [{ message: 'Required' }];
		}

		if (typeof model.qualifier !== 'number') {
			errors.qualifier = [{ message: 'Required' }];
		}

		if (!this.args.model.deliveryType.length) {
			errors.delivery = [{ message: 'Required' }];
		}

		if (model.metricSetupKey) {
			if (!model.metricSetup.isFilledSingleOutput) {
				errors.attribute = [{ message: 'Required' }];
				errors.unit = [{ message: 'Required' }];
			}
		} else {
			errors.metric = [{ message: 'Required' }];
		}

		return errors;
	}

	@action
	save() {
		const model = this.args.model;

		const userIds = Array.from(model.userIds);
		const currentUserId = this.currentUserService.numericId;
		if (currentUserId && !userIds.includes(currentUserId)) {
			userIds.push(currentUserId);
		}
		model.userIds = userIds;

		const isNew = model.isNew;

		return model.save().then(() => {
			if (isNew) {
				this.analyticsService.event(
					TrackingEvent.ALERT_CREATED,
					Object.assign({ origin: this.args.origin }, model.trackingInfo),
				);
			} else {
				this.analyticsService.event(TrackingEvent.ALERT_MODIFIED, {
					origin: this.args.origin,
				});
			}
		});
	}

	<template>
		{{#if @model.isNew}}
			<NotificationsEditFormMetricSetupRowComponent
				@label="Get notified when"
				@metricSetup={{@model.metricSetup}}
				@isDisabled={{@submitInProgress}}
				@errors={{@errors}}
			/>
		{{/if}}
		<FormRowComponent
			@label={{if @model.isNew "" "Get notified"}}
			@className="row--metric-setup"
		>
			<div class="selector selector-metric">
				<FormSelectComponent
					name="condition"
					@selected={{@model.condition}}
					@options={{this.eventConditions}}
					@onChange={{this.changeCondition}}
					@isRequired={{true}}
					@isDisabled={{@submitInProgress}}
					@placeholder="Condition"
				/>
				<RowErrorComponent @items={{@model.errors.condition}} />
				<RowErrorComponent @items={{@errors.condition}} />
			</div>
			<div class="selector selector-item">
				{{#if @model.metricSetup.metric.hasPercentFormat}}
					<FormInputPercentComponent
						@value={{this.qualifierString}}
						@onChange={{this.changeQualifier}}
						@placeholder="Qualifier"
						@isDisabled={{@submitInProgress}}
						@format={{@model.metricSetup.metric.format}}
						@tooltip={{hash text="This is a percentage metric"}}
					/>
				{{else}}
					<FormInputDecimalComponent
						@value={{this.qualifierString}}
						@onChange={{this.changeQualifier}}
						@placeholder="Qualifier"
						@isDisabled={{@submitInProgress}}
					/>
				{{/if}}
				<RowErrorComponent @items={{@model.errors.qualifier}} />
				<RowErrorComponent @items={{@errors.qualifier}} />
			</div>
		</FormRowComponent>
		<FormRowComponent @label="in the period of" @className="row--dropdown row--mb-more">
			<div class="selector selector--size-2">
				<FormSelectComponent
					name="range"
					@selected={{@model.selectedRangeId}}
					@options={{@model.rangeOptions}}
					@onChange={{this.changeRange}}
					@isRequired={{true}}
					@isDisabled={{@submitInProgress}}
					@placeholder="Date range"
				/>
			</div>
		</FormRowComponent>

		<FormRowComponent @label="Frequency" @className="row--radiobuttons">
			<FormRadiobuttonGroupComponent
				class="radiobutton-default"
				@name="deliveryTimeType"
				@selected={{@model.deliveryTime}}
				@options={{this.deliveryTimeOptions}}
				@isDisabled={{@submitInProgress}}
				@onChange={{this.changeDeliveryTimeType}}
			/>
		</FormRowComponent>

		<NotificationsDeliveryTargetsComponent
			@selected={{@model.deliveryType}}
			@slackChannels={{@model.slackChannels}}
			@setDefaults={{@model.isNew}}
			@isDisabled={{@submitInProgress}}
			@onSelectionChange={{this.changeDelivery}}
			@onSlackChannelsChange={{this.changeSlackChannels}}
		>
			<RowErrorComponent @items={{@errors.delivery}} />
		</NotificationsDeliveryTargetsComponent>

		{{#if this.isRecipientsShown}}
			<FormRowComponent @label="Recipient(s)" @className="row--radiobuttons">
				<CommonRecipientsSelectorComponent
					@model={{@model}}
					@submitInProgress={{@submitInProgress}}
				/>
			</FormRowComponent>
		{{/if}}

		<div class="alert-advanced-settings">
			<SectionWithTogglerComponent
				@isOpen={{this.isAdvancedOpen}}
				@title="Advanced settings"
				@toggle={{this.toggleAdvanced}}
			>
				<div class="related-warning">
					<CommonUiIcon @name="AlertFill" @size="sm" />
					Related metrics have been deprecated.
				</div>

				<p>
					Customize the messaging in your alert by adding your own text or clicking on an
					input field below.
				</p>

				<NotificationsAlertsCustomTemplateComponent @model={{@model}} />
			</SectionWithTogglerComponent>
		</div>
	</template>
}
